"use client";

import { memo, useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

export interface StaticMapMarker {
  lat: number;
  lng: number;
  /** Optional per-marker color (Static Maps API hex, e.g. "0xEA580C"). */
  color?: string;
  /** Use a smaller marker when many locations share the map. */
  size?: "tiny" | "mid";
}

interface StaticMapImageProps {
  /** Markers to render. The first marker is used as the default center when `center` is not provided. */
  markers?: StaticMapMarker[];
  /** Map center. Defaults to the first marker. */
  center?: StaticMapMarker;
  zoom?: number;
  /** Optional caption rendered as an overlay at the bottom. */
  title?: string;
  /** Marker color (Static Maps API hex, e.g. "0xEA580C"). */
  markerColor?: string;
  showMarkers?: boolean;
  loading?: "eager" | "lazy";
  className?: string;
  alt?: string;
}

// Orange-600 — matches the lucide MapPin color used across the dynamic maps.
const DEFAULT_MARKER_COLOR = "0xEA580C";

function StaticMapFallback() {
  // map.json is spread at the top level of the messages object (see messages/<locale>/index.ts:
  // `...map`), so the namespace is `errors`, not `map.errors`.
  const t = useTranslations("errors");
  return (
    <div className="h-full w-full flex items-center justify-center bg-gray-100">
      <div className="flex flex-col items-center gap-2 text-center px-4">
        <MapPin className="h-6 w-6 text-gray-400" />
        <p className="text-sm text-gray-500">{t("mapLoadError")}</p>
      </div>
    </div>
  );
}

/**
 * Read-only map rendered via the Google Maps Static API (`<img>`).
 *
 * Replaces the dynamic Maps JS SDK (`@vis.gl/react-google-maps`) for non-interactive
 * surfaces. Static Maps is a separate, cheaper SKU (own 10k/month free tier, ~$2/1k vs
 * Dynamic ~$7/1k) and produces a deterministic URL so the browser/CDN cache serves repeat
 * views without a new billable request. Custom marker icons must be PNG (no SVG), so the
 * lucide MapPin is replaced with a colored Static API marker.
 */
// Google Static Maps caps the base `size` at 640px per side (scale handles retina).
const MAX_STATIC_SIZE = 640;

function StaticMapImage({
  markers = [],
  center,
  zoom = 15,
  title,
  markerColor = DEFAULT_MARKER_COLOR,
  showMarkers = true,
  loading = "lazy",
  className,
  alt,
}: StaticMapImageProps) {
  const locale = useLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasError, setHasError] = useState(false);
  // Keep the requested map's aspect ratio equal to its container. Static Maps
  // caps each side at 640px, so larger containers must scale both sides together.
  // Measured before the first fetch to avoid a second request at a different size.
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const scale = Math.min(1, MAX_STATIC_SIZE / Math.max(rect.width, rect.height));
        setSize({
          w: Math.max(1, Math.round(rect.width * scale)),
          h: Math.max(1, Math.round(rect.height * scale)),
        });
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const validMarkers = markers.filter(
    (m) =>
      m.lat != null &&
      m.lng != null &&
      !Number.isNaN(m.lat) &&
      !Number.isNaN(m.lng),
  );

  const resolvedCenter = center ?? validMarkers[0];

  if (hasError || !resolvedCenter) {
    return (
      <div ref={containerRef} className={cn("h-full w-full", className)}>
        <StaticMapFallback />
      </div>
    );
  }

  let src: string | null = null;
  if (size) {
    const params = new URLSearchParams();
    params.set("center", `${resolvedCenter.lat},${resolvedCenter.lng}`);
    params.set("zoom", String(zoom));
    params.set("size", `${size.w}x${size.h}`);
    params.set("scale", "2");
    params.set("language", locale);
    if (showMarkers) {
      const markersByStyle = new Map<string, StaticMapMarker[]>();
      for (const marker of validMarkers) {
        const color = marker.color ?? markerColor;
        const style = `${color}|${marker.size ?? "normal"}`;
        markersByStyle.set(style, [...(markersByStyle.get(style) ?? []), marker]);
      }
      for (const [style, styleMarkers] of markersByStyle) {
        const [color, markerSize] = style.split("|");
        params.append(
          "markers",
          [
            `color:${color}`,
            ...(markerSize === "normal" ? [] : [`size:${markerSize}`]),
            ...styleMarkers.map((marker) => `${marker.lat},${marker.lng}`),
          ].join("|"),
        );
      }
    }
    src = `/api/static-map?${params.toString()}`;
  }

  return (
    <div
      ref={containerRef}
      className={cn("relative h-full w-full bg-gray-100", className)}
    >
      {src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={alt ?? title ?? "map"}
          loading={loading}
          decoding="async"
          className="h-full w-full object-fill"
          onError={() => setHasError(true)}
        />
      )}
      {title && (
        <div className="absolute bottom-3 left-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-md">
          <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
        </div>
      )}
    </div>
  );
}

export default memo(StaticMapImage);
